import React from "react";
import { Exercise, ExerciseReference } from "../../api/types.ts";
import { useDialog } from "../DialogManager.tsx";
import { isNotNullish } from "../../util/typeGuards.ts";
import { convertResistance, getAverageStats } from "../../util/exercise.tsx";
import { ExerciseSetButton } from "../widgets/ExerciseSetButton.tsx";

interface RoutineExerciseSetsProps {
  exercise: Exercise; // The refrenced exercise from the library
  routineExercise: ExerciseReference; // Resistance and set details specific to the routine exercise
  routineExerciseIndex: number;
  onAddSet?: (
    routineExerciseIndex: number,
    repetitions: number,
    resistance: number,
  ) => void;
  onEditSet?: (
    routineExerciseIndex: number,
    setIndex: number,
    repetitions: number,
    resistance: number,
  ) => void;
  isEditable?: boolean;
  isShared?: boolean;
}

const RoutineExerciseSets: React.FC<RoutineExerciseSetsProps> = ({
  exercise,
  routineExercise,
  routineExerciseIndex,
  onAddSet,
  onEditSet,
  isEditable = false,
  isShared = false,
}) => {
  const { openDialog } = useDialog();

  const convertedStartResistance = convertResistance(
    exercise.start_resistance,
    exercise.default_resistance_unit,
    routineExercise.resistance_unit,
  );

  const getDisplayResistance = (resistance: number): number => {
    return convertedStartResistance + exercise.efficiency_factor * resistance;
  };

  const handleOpenDialog = (e: React.MouseEvent, setIndex?: number) => {
    e.stopPropagation();

    const set = setIndex !== undefined ? routineExercise.sets[setIndex] : null;

    // Pre-fill with last set values if available
    const dialogProps: any = {
      resistanceUnit: routineExercise.resistance_unit,
      exercise: exercise,
      onAddSet: (repetitions: number, resistance: number) =>
        onAddSet && onAddSet(routineExerciseIndex, repetitions, resistance),
      onEditSet: (repetitions: number, resistance: number) =>
        onEditSet &&
        onEditSet(
          routineExerciseIndex,
          setIndex ?? -1,
          repetitions,
          resistance,
        ),
    };

    if (set) {
      dialogProps.repetitions = set.repetitions.toString();
      dialogProps.resistance = set.resistance.toString();
      dialogProps.editMode = true;
    } else if (routineExercise.sets.length > 0) {
      const lastSet = routineExercise.sets[routineExercise.sets.length - 1];
      dialogProps.repetitions = lastSet.repetitions.toString();
      dialogProps.resistance = lastSet.resistance.toString();
    }

    openDialog(isNotNullish(setIndex) ? "edit-set" : "add-set", dialogProps);
  };

  return (
    <>
      <div className="pt-2 flex flex-wrap gap-2 justify-start items-center">
        {routineExercise.sets.map((set, setIndex) => (
          <ExerciseSetButton
            key={setIndex}
            onClick={
              isShared ? undefined : (e) => handleOpenDialog(e, setIndex)
            }
            isEditable={!!onEditSet && isEditable}
          >
            <span className="text-nowrap">
              {set.repetitions} ×{" "}
              {Math.round(getDisplayResistance(set.resistance))}
              {routineExercise.resistance_unit}
            </span>
          </ExerciseSetButton>
        ))}

        {onAddSet && (
          <ExerciseSetButton
            onClick={handleOpenDialog}
            isAddButton
            isEditable={isEditable}
          />
        )}
      </div>
      {exercise.start_resistance > 0 && (
        <p className="text-xs text-gray-600 mt-2 italic">
          *Including start resistance of {Math.round(convertedStartResistance)}
          {routineExercise.resistance_unit}
        </p>
      )}
      <p className="text-xs text-gray-600 mt-2 sm:justify-self-end">
        {getAverageStats({
          ...routineExercise,
          start_resistance: convertedStartResistance,
          efficiency_factor: exercise.efficiency_factor,
        }) || "No sets recorded"}
      </p>
    </>
  );
};

export { RoutineExerciseSets };
